package com.eladfinish.bible.contest.screens.main

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.ads.ShouldShowAdsUseCase
import com.eladfinish.bible.contest.ads_already_disabled_format
import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.analytics.SetUserPropertiesUseCase
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import com.eladfinish.bible.contest.analytics.usageStatsEnabled
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.common.json
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.di.config.AppInfo
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.features.review.ReviewPrompter
import com.eladfinish.bible.contest.features.share.ShareAppUseCaseCommon
import com.eladfinish.bible.contest.features.teruma.AboutTerumaUiModel
import com.eladfinish.bible.contest.features.teruma.ResolveAboutTerumaUseCase
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetPromptSession
import com.eladfinish.bible.contest.features.verseWidget.isVerseWidgetPlatform
import com.eladfinish.bible.contest.help_txt
import com.eladfinish.bible.contest.mikud
import com.eladfinish.bible.contest.platform.isAndroid
import com.eladfinish.bible.contest.platform.isIos
import com.eladfinish.bible.contest.platform.isStartupSystemPromptPending
import com.eladfinish.bible.contest.platform.isWeb
import com.eladfinish.bible.contest.platform.platformSpecific
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfigRepository
import com.eladfinish.bible.contest.repository.remoteConfig.get
import com.eladfinish.bible.contest.screens.main.MainScreenState.DialogState
import com.eladfinish.bible.contest.screens.main.data.ButtonData
import com.eladfinish.bible.contest.screens.main.data.ButtonType
import com.eladfinish.bible.contest.screens.main.data.ItemMode
import com.eladfinish.bible.contest.screens.main.data.toButtonDataList
import com.eladfinish.bible.contest.screens.main.featureSheet.firstSheetDue
import com.eladfinish.bible.contest.screens.main.featureSheet.isOnUnlessDisabled
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.PastQuizzesSheetGate
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.PastQuizzesSource
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.pastQuizzesOpenedParams
import com.eladfinish.bible.contest.screens.main.usecases.EvenButtonGridUseCase
import com.eladfinish.bible.contest.screens.main.usecases.GetAnnouncementUseCase
import com.eladfinish.bible.contest.screens.main.usecases.GetMainScreenButtonListUseCase
import com.eladfinish.bible.contest.screens.main.usecases.GetRemoteFileContentUseCase
import com.eladfinish.bible.contest.screens.main.usecases.GetTelegramChannelUrlUseCase
import com.eladfinish.bible.contest.screens.main.verseWidgetSheet.VerseWidgetSheetGate
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetChaptersInMikudAsArrayListUseCase
import com.eladfinish.bible.contest.screens.pastQuizzes.examFontPreference
import com.eladfinish.bible.contest.screens.pastQuizzes.pastQuizzesUrl
import com.eladfinish.bible.contest.ui.components.bottomSheet.FeatureSheetDismissMethod
import com.eladfinish.bible.contest.ui.menus.actions.MainToolbarAction
import com.eladfinish.bible.contest.utils.formatTime
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.desc
import dev.icerock.moko.resources.format
import io.github.aakira.napier.Napier
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

class MainScreenStore(
    private val appInfo: AppInfo,
    private val analyticsProvider: AnalyticsProvider,
    private val buttonListOrderRepository: ButtonListOrderRepository,
    private val debugInfo: DebugInfo,
    private val getMainScreenButtonListUseCase: GetMainScreenButtonListUseCase,
    private val getRemoteFileContentUseCase: GetRemoteFileContentUseCase,
    private val getAnnouncementUseCase: GetAnnouncementUseCase,
    private val getTelegramChannelUrlUseCase: GetTelegramChannelUrlUseCase,
    private val getChaptersInMikudAsArrayListUseCase: GetChaptersInMikudAsArrayListUseCase,
    private val mikudRepository: MikudRepository,
    private val assetReader: AssetReader,
    private val setUserPropertiesUseCase: SetUserPropertiesUseCase,
    private val resolveAboutTerumaUseCase: ResolveAboutTerumaUseCase,
    private val shouldShowAdsUseCase: ShouldShowAdsUseCase,
    private val shareAppUseCaseCommon: ShareAppUseCaseCommon,
    private val evenButtonGridUseCase: EvenButtonGridUseCase,
    private val reviewPrompter: ReviewPrompter,
    val settings: Settings,
    private val remoteConfigRepository: RemoteConfigRepository,
    private val pastQuizzesSheetGate: PastQuizzesSheetGate,
    private val verseWidgetSheetGate: VerseWidgetSheetGate,
    private val verseWidgetPromptSession: VerseWidgetPromptSession,
) : ViewModelStore<MainScreenState, MainScreenAction, MainScreenEffect>(
    tag = "MainScreenStore",
) {

    // First of all, before this launch's gender dialog can be answered: whether the user had the
    // app before each "what's new" sheet existed (see OneTimeSheetGate).
    init {
        pastQuizzesSheetGate.captureExistingUser()
        verseWidgetSheetGate.captureExistingUser()
    }

    /**
     * The gender dialog or a system prompt (iOS tracking) is due this launch: the "what's new"
     * sheet never shares a launch with either, and waits for the next one.
     */
    private val isStartupPromptDueAtLaunch =
        !settings.getBoolean(key = Constants.PREF_HEBREW_GENDER_LANGUAGE_SET, defaultValue = false) ||
            isStartupSystemPromptPending()

    // stateFlow (and the defaultState/initialButtonDataList/... chain it depends on) must be
    // declared — and therefore initialized — before the init block below: that block's
    // launchEffect schedules work on Dispatchers.Default, a real thread pool on JVM targets
    // (desktop in particular), which can start running before this constructor reaches a
    // later property initializer in source order and read stateFlow while it's still null.
    // Kotlin runs property initializers and init blocks strictly in declaration order, so this
    // is a plain reordering fix, not a behavior change.
    /** Read on every tap, so a Remote Config value fetched after launch is picked up. */
    val telegramChannelUrl: String
        get() = getTelegramChannelUrlUseCase()

    private val mainScreenButtonList: List<ButtonType>
        get() = getMainScreenButtonListUseCase(isDebugMode = debugInfo.isDebugMode)

    /** The "חידונים אמיתיים" page, for the targets that open it in the browser (web, desktop). */
    fun pastQuizzesUrl(isDarkTheme: Boolean): String = pastQuizzesUrl(
        remoteBaseUrl = runCatching { remoteConfigRepository.get<String>(RemoteConfig.PAST_QUIZZES_URL) }.getOrNull(),
        isDarkTheme = isDarkTheme,
        fontPreference = settings.examFontPreference(),
        hebrewGender = settings.getHebrewGender(),
        usageStats = settings.usageStatsEnabled(),
    )

    fun onPastQuizzesOpened(inApp: Boolean, source: PastQuizzesSource) {
        analyticsProvider.logEvent(
            AnalyticsConstants.PastQuizzes.EVENT_OPENED,
            pastQuizzesOpenedParams(inApp = inApp, source = source),
        )
    }

    private val buttonListOrderData: String
        get() = buttonListOrderRepository.read()

    private val buttonTypeList: List<ButtonType>
        get() = buttonListOrderData.convertToDataClass<List<ButtonType>>()
            .filterNot { it == ButtonType.UNKNOWN }
            .takeIf { it.isNotEmpty() } ?: mainScreenButtonList

    /**
     * The "חידונים אמיתיים" button unless Remote Config switched it off. Read again after every fetch
     * (see [MainScreenAction.RefreshButtons]); a missing parameter or a failed read means on.
     */
    private val isPastQuizzesEnabled: Boolean
        get() = remoteConfigRepository.isOnUnlessDisabled(RemoteConfig.DISABLE_PAST_QUIZZES)

    /**
     * The widget's button: Android and iOS only, unless Remote Config switched it off (a missing
     * parameter or a failed read means on).
     */
    private val isVerseWidgetEnabled: Boolean
        get() = isVerseWidgetPlatform && remoteConfigRepository.isOnUnlessDisabled(RemoteConfig.DISABLE_VERSE_WIDGET)

    private val finalButtonTypeList: List<ButtonType>
        get() = buttonTypeList
        .toMutableList()
        .apply {
            add(buttonTypeList.size - 2, ButtonType.RECORDINGS)
            // Drag-to-reorder is plain Compose pointer input with no platform-specific API, so it
            // works on web too (unlike e.g. checkUpdate()/consumeTestPurchase(), which are no-ops
            // there) — keep it available on both Android and web.
            if (isAndroid || isWeb) {
                add(buttonTypeList.size - 1, ButtonType.REORDER)
            }
        }.distinct().withTelegramChannel().withPastQuizzes(isEnabled = isPastQuizzesEnabled)
            .withVerseWidget(isEnabled = isVerseWidgetEnabled)

    private val initialButtonTypeList: List<ButtonType> = finalButtonTypeList

    private val initialButtonDataList = evenButtonGridUseCase.gridButtonTypes(initialButtonTypeList).toButtonDataList()

    private val defaultState: MainScreenState
        get() = MainScreenState(
            buttonDataList = initialButtonDataList.toImmutableList(),
            itemMode = ItemMode.REGULAR,
            isDebugMode = debugInfo.isDebugMode,
            hebrewGender = settings.getHebrewGender(),
            hebrewGenderDialogAlreadyShown = settings.getBoolean(key = Constants.PREF_HEBREW_GENDER_LANGUAGE_SET, defaultValue = false),
            announcement = platformSpecific(forAndroid = { "" }, forIos = { "" }),
            aboutTerumaUiModel = null,
            shouldShowAds = false,
            canPurchaseAdFree = canPurchaseAdFree(),
            dialogState = DialogState.None,
            isHelpInToolbar = evenButtonGridUseCase.isHelpInToolbar(initialButtonTypeList),
        )

    @ExperimentalCoroutinesApi
    override val stateFlow = MutableStateFlow(defaultState)

    init {
        launchEffect {
            val result = remoteConfigRepository.fetchAndActivate()
            Napier.d("Firebase RemoteConfig fetchAndActivate $result")
            remoteConfigRepository.setMinimumFetchInterval(if (debugInfo.isDebugMode) 0.seconds else 3600.seconds)
            val defaults = mapOf(
                "NUMBER_OF_QUESTIONS" to 10,
                "announcement" to "ברוכים הבאים",
                "openQuestionsLastEditServer" to "01.01.2016",
                "DATES_OF_FREE_MIKUD" to "[]",
                "enableFreeServerMikud" to false,
            )
            remoteConfigRepository.setDefaults(defaults)
            remoteConfigRepository.getAll().map { (key, value) ->
                Napier.d(tag = "MainScreenStore", message = "Firebase RemoteConfig key value: $key = ${value.asString()}")
            }

            val aboutTerumaUiModel = resolveAboutTerumaUseCase()
            aboutTerumaUiModel?.let {
                dispatch(MainScreenAction.ShowAboutTeruma(aboutTerumaUiModel = it))
            }

            dispatch(MainScreenAction.UpdateAnnouncement(text = getAnnouncementUseCase()))
            // Remote Config may have just switched a button on or off (DISABLE_PAST_QUIZZES)
            dispatch(MainScreenAction.RefreshButtons)
            // Decided only now, so that a switch fetched on this launch already applies.
            // One sheet per launch, in this order; the other waits for the next launch.
            when (firstSheetDue(isStartupPromptDueAtLaunch, listOf(pastQuizzesSheetGate, verseWidgetSheetGate))) {
                pastQuizzesSheetGate -> dispatch(MainScreenAction.OfferPastQuizzesSheet)
                verseWidgetSheetGate -> dispatch(MainScreenAction.OfferVerseWidgetSheet)
                else -> Unit
            }

            launchEffect {
                while (true) {
                    dispatch(
                        MainScreenAction.UpdateShowAds(
                            showAds = shouldShowAdsUseCase()
                        )
                    )
                    delay(if (debugInfo.isDebugMode) 10.seconds else 1.minutes)
                }
            }

            setUserPropertiesUseCase()
        }
    }

    override fun handleAction(action: MainScreenAction): MainScreenState =
        when (action) {
            is MainScreenAction.ButtonClicked -> handleButtonClicked(action)
            is MainScreenAction.DoneDragMode -> handleDoneDragMode(action)
            MainScreenAction.SwitchToDragMode -> handleSwitchToDragMode()
            MainScreenAction.CancelDragMode -> handleCancelDragMode()
            MainScreenAction.ResetOrder -> handleResetOrder()
            is MainScreenAction.UpdateHebrewGender -> handleUpdateHebrewGender(action)
            MainScreenAction.ReadHebrewGender -> handleReadHebrewGender()
            is MainScreenAction.UpdateAnnouncement -> handleUpdateAnnouncement(action)
            is MainScreenAction.UpdateShowAds -> handleUpdateShowAds(action)
            MainScreenAction.RefreshButtons -> handleRefreshButtons()
            is MainScreenAction.ShowAboutTeruma -> handleShowAboutTeruma(action.aboutTerumaUiModel)
            MainScreenAction.AnnouncementClicked -> handleAnnouncementClicked()
            is MainScreenAction.ToolbarClicked -> handleToolbarClicked(action.toolbarAction)
            MainScreenAction.AboutClicked -> handleAboutClicked()
            MainScreenAction.AboutLongClicked -> handleAboutLongClicked()
            MainScreenAction.MikudLongClicked -> handleMikudLongClicked()
            MainScreenAction.HelpClicked -> handleHelpClicked()
            MainScreenAction.ConfirmedSupportAd -> handleConfirmedSupportAd()
            MainScreenAction.HideDialog -> handleHideDialog()
            MainScreenAction.ConfirmedRewardedAd -> handleConfirmedRewardedAd()
            is MainScreenAction.RemoveAdsClicked -> handleRemoveAdsClicked(action)
            MainScreenAction.RemoveAdsButtonShown -> handleRemoveAdsButtonShown()
            MainScreenAction.RestorePurchaseClicked -> handleRestorePurchaseClicked()
            MainScreenAction.ConsumeTestPurchaseClicked -> handleConsumeTestPurchaseClicked()
            MainScreenAction.ShowRewardEarnedUpsell -> handleShowRewardEarnedUpsell()
            MainScreenAction.OfferPastQuizzesSheet -> currentState.copy(isPastQuizzesSheetOffered = true)
            MainScreenAction.ShowPastQuizzesSheet -> handleShowPastQuizzesSheet()
            is MainScreenAction.PastQuizzesSheetDismissed -> handlePastQuizzesSheetDismissed(action.method)
            MainScreenAction.PastQuizzesSheetCtaClicked -> handlePastQuizzesSheetCtaClicked()
            MainScreenAction.OfferVerseWidgetSheet -> currentState.copy(isVerseWidgetSheetOffered = true)
            MainScreenAction.ShowVerseWidgetSheet -> handleShowVerseWidgetSheet()
            is MainScreenAction.VerseWidgetSheetDismissed -> handleVerseWidgetSheetDismissed(action.method)
            MainScreenAction.VerseWidgetSheetCtaClicked -> handleVerseWidgetSheetCtaClicked()
        }

    /**
     * The home screen has been idle with the sheet offered: show it, and mark it shown right now
     * rather than when it closes, so that a crash or a kill never brings it back.
     */
    private fun handleShowPastQuizzesSheet(): MainScreenState {
        if (!currentState.isPastQuizzesSheetOffered) return currentState
        // Remote Config may have changed while the screen waited.
        if (!pastQuizzesSheetGate.shouldShow()) return currentState.copy(isPastQuizzesSheetOffered = false)
        pastQuizzesSheetGate.markShown()
        analyticsProvider.logEvent(AnalyticsConstants.PastQuizzes.EVENT_SHEET_SHOWN)
        return currentState.copy(isPastQuizzesSheetOffered = false, isPastQuizzesSheetVisible = true)
    }

    private fun handlePastQuizzesSheetDismissed(method: FeatureSheetDismissMethod): MainScreenState {
        if (!currentState.isPastQuizzesSheetVisible) return currentState
        analyticsProvider.logEvent(
            AnalyticsConstants.PastQuizzes.EVENT_SHEET_DISMISSED,
            mapOf(AnalyticsConstants.FeatureSheet.PARAM_METHOD to method.analyticsValue),
        )
        return currentState.copy(isPastQuizzesSheetVisible = false)
    }

    /** Only the sheet's part; the screen then opens חידונים אמיתיים the way its button does. */
    private fun handlePastQuizzesSheetCtaClicked(): MainScreenState {
        if (!currentState.isPastQuizzesSheetVisible) return currentState
        analyticsProvider.logEvent(AnalyticsConstants.PastQuizzes.EVENT_SHEET_CTA)
        return currentState.copy(isPastQuizzesSheetVisible = false)
    }

    /** Same as [handleShowPastQuizzesSheet], for the widget's sheet. */
    private fun handleShowVerseWidgetSheet(): MainScreenState {
        if (!currentState.isVerseWidgetSheetOffered) return currentState
        if (!verseWidgetSheetGate.shouldShow()) return currentState.copy(isVerseWidgetSheetOffered = false)
        verseWidgetSheetGate.markShown()
        // The hint on the random-verse screen never shares a session with this sheet.
        verseWidgetPromptSession.onOtherPromptShown()
        analyticsProvider.logEvent(AnalyticsConstants.VerseWidget.EVENT_SHEET_SHOWN)
        return currentState.copy(isVerseWidgetSheetOffered = false, isVerseWidgetSheetVisible = true)
    }

    private fun handleVerseWidgetSheetDismissed(method: FeatureSheetDismissMethod): MainScreenState {
        if (!currentState.isVerseWidgetSheetVisible) return currentState
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_SHEET_DISMISSED,
            mapOf(AnalyticsConstants.FeatureSheet.PARAM_METHOD to method.analyticsValue),
        )
        return currentState.copy(isVerseWidgetSheetVisible = false)
    }

    /** Only the sheet's part; the screen then opens the widget's settings the way its tile does. */
    private fun handleVerseWidgetSheetCtaClicked(): MainScreenState {
        if (!currentState.isVerseWidgetSheetVisible) return currentState
        analyticsProvider.logEvent(AnalyticsConstants.VerseWidget.EVENT_SHEET_CTA)
        return currentState.copy(isVerseWidgetSheetVisible = false)
    }

    private fun handleButtonClicked(action: MainScreenAction.ButtonClicked): MainScreenState {
        analyticsProvider.logEvent(
            AnalyticsConstants.MainButton.EVENT_CLICKED,
            mainButtonClickedParams(buttonType = action.buttonType, position = action.position),
        )
        return currentState
    }

    private fun handleDoneDragMode(action: MainScreenAction.DoneDragMode): MainScreenState {
        val newOrder: List<Pair<ButtonType, ButtonData>> = action.newOrder
        val buttonTypeList = newOrder.map { it.first }
        val buttonTypeListString = json.encodeToString(buttonTypeList)
        buttonListOrderRepository.save(buttonTypeListString)
        analyticsProvider.logEvent(REORDER_EVENT_NAME, mapOf(REORDER_PROPERTY to buttonTypeListString))
        return currentState.copy(
            itemMode = ItemMode.REGULAR,
            buttonDataList = action.newOrder.toImmutableList(),
        )
    }

    private fun handleSwitchToDragMode(): MainScreenState {
        return currentState.copy(
            itemMode = ItemMode.DRAGGABLE,
        )
    }

    private fun handleCancelDragMode(): MainScreenState {
        // A saved order from before the Telegram button existed must not lose it here.
        val buttonTypes = buttonTypeList.withTelegramChannel().withPastQuizzes(isEnabled = isPastQuizzesEnabled)
            .withVerseWidget(isEnabled = isVerseWidgetEnabled)
        return currentState.copy(
            itemMode = ItemMode.REGULAR,
            buttonDataList = evenButtonGridUseCase.gridButtonTypes(buttonTypes).toButtonDataList(),
            isHelpInToolbar = evenButtonGridUseCase.isHelpInToolbar(buttonTypes),
        )
    }

    private fun handleResetOrder(): MainScreenState {
        val buttonTypes = mainScreenButtonList.withPastQuizzes(isEnabled = isPastQuizzesEnabled)
            .withVerseWidget(isEnabled = isVerseWidgetEnabled)
        return currentState.copy(
            itemMode = ItemMode.REGULAR,
            buttonDataList = evenButtonGridUseCase.gridButtonTypes(buttonTypes).toButtonDataList(),
            isHelpInToolbar = evenButtonGridUseCase.isHelpInToolbar(buttonTypes),
        )
    }

    /** Rebuilds the grid from the saved order and the current Remote Config; never mid-reorder. */
    private fun handleRefreshButtons(): MainScreenState {
        if (currentState.itemMode != ItemMode.REGULAR) return currentState
        val buttonTypes = finalButtonTypeList
        return currentState.copy(
            buttonDataList = evenButtonGridUseCase.gridButtonTypes(buttonTypes).toButtonDataList(),
            isHelpInToolbar = evenButtonGridUseCase.isHelpInToolbar(buttonTypes),
        )
    }

    private fun handleUpdateHebrewGender(action: MainScreenAction.UpdateHebrewGender): MainScreenState {
        val hebrewGender = action.hebrewGender.name.lowercase()
        analyticsProvider.logEvent(
            AnalyticsConstants.SetHebrewGender.EVENT_FIRST_LAUNCH, mapOf(
                AnalyticsConstants.SetHebrewGender.GENDER to hebrewGender,
            )
        )
        settings.putBoolean(key = Constants.PREF_HEBREW_GENDER_LANGUAGE_SET, value = true)
        settings.putString(key = Constants.PREF_HEBREW_GENDER_LANGUAGE, value = action.hebrewGender.name.lowercase())
        return currentState.copy(
            hebrewGender = action.hebrewGender,
            hebrewGenderDialogAlreadyShown = true,
        )
    }

    private fun handleReadHebrewGender(): MainScreenState {
        return currentState.copy(
            hebrewGender = settings.getHebrewGender()
        )
    }

    private fun handleUpdateAnnouncement(action: MainScreenAction.UpdateAnnouncement): MainScreenState {
        return currentState.copy(
            announcement = action.text
        )
    }

    private fun handleUpdateShowAds(action: MainScreenAction.UpdateShowAds): MainScreenState {
        return currentState.copy(
            shouldShowAds = action.showAds,
            canPurchaseAdFree = canPurchaseAdFree(),
        )
    }

    // Offered whenever the purchase hasn't been made — deliberately NOT gated on shouldShowAds,
    // which is false during a rewarded ad-free window. Gating on it once hid the purchase from
    // App Review (iOS 1.0.16 rejection, Guideline 2.1(b)). Only Android and iOS have a store to
    // buy from.
    private fun canPurchaseAdFree(): Boolean =
        (isAndroid || isIos) && !settings.getBoolean(SharedPrefsConstants.AD_FREE_PURCHASED, false)

    private fun handleAnnouncementClicked(): MainScreenState {
        return currentState
    }

    private fun handleShowRewardEarnedUpsell(): MainScreenState {
        return currentState.copy(dialogState = DialogState.RewardEarnedUpsell)
    }

    private fun handleRemoveAdsClicked(action: MainScreenAction.RemoveAdsClicked): MainScreenState {
        analyticsProvider.logEvent(
            event = AnalyticsConstants.Purchase.EVENT_CLICKED,
            params = mapOf(
                AnalyticsConstants.Purchase.PARAM_SOURCE to when (action.source) {
                    RemoveAdsSource.MENU_BUTTON -> AnalyticsConstants.Purchase.SOURCE_MENU_BUTTON
                    RemoveAdsSource.REWARD_UPSELL -> AnalyticsConstants.Purchase.SOURCE_REWARD_UPSELL
                },
            ),
        )
        onlyEffect(MainScreenEffect.StartRemoveAdsPurchase)
        return handleHideDialog()
    }

    private fun handleRestorePurchaseClicked(): MainScreenState {
        onlyEffect(MainScreenEffect.RestorePurchase)
        return currentState
    }

    private fun handleConsumeTestPurchaseClicked(): MainScreenState {
        onlyEffect(MainScreenEffect.ConsumeTestPurchase)
        return currentState
    }

    private fun handleConfirmedSupportAd(): MainScreenState {
        onlyEffect(MainScreenEffect.ShowSupportAd)
        analyticsProvider.logEvent(AnalyticsConstants.CONFIRM_SHOW_AD)
        return handleHideDialog()
    }

    private fun handleConfirmedRewardedAd(): MainScreenState {
        onlyEffect(MainScreenEffect.ShowRewardedAd)
        analyticsProvider.logEvent(AnalyticsConstants.CONFIRM_SHOW_REWARDED_AD)
        return handleHideDialog()
    }

    private fun handleMikudLongClicked(): MainScreenState {
        return currentState.copy(
            dialogState = DialogState.DebugStuff(
                title = SharedRes.strings.mikud.desc(),
                content = buildString {
                    val mikudList = mikudRepository.mikudList
                    appendLine("mikudList: $mikudList")
                    appendLine("\n--------\n")
                    appendLine("chaptersInMikud: ${getChaptersInMikudAsArrayListUseCase(mikudStr = mikudList.trim())}")
                },
            )
        )
    }

    private fun handleAboutClicked(): MainScreenState {
        analyticsProvider.logEvent(event = AnalyticsConstants.SHOW_ABOUT)
        return currentState.copy(
            dialogState = DialogState.About(
                appInfo = appInfo,
                aboutTerumaUiModel = currentState.aboutTerumaUiModel,
            )
        )
    }

    private fun handleAboutLongClicked(): MainScreenState {
        launchEffect {
            val content = getRemoteFileContentUseCase()
            updateState {
                it.copy(
                    dialogState = DialogState.DebugStuff(
                        title = "TEST".desc(),
                        content = content,
                    )
                )
            }
        }
        return currentState
    }

    private fun handleHelpClicked(): MainScreenState {
        analyticsProvider.logEvent(event = AnalyticsConstants.SHOW_MAIN_HELP)
        return currentState.copy(
            dialogState = DialogState.Help(
                content = assetReader.readFile(SharedRes.assets.other.help_txt)
            )
        )
    }

    private fun handleHideDialog(): MainScreenState {
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleToolbarClicked(toolbarAction: MainToolbarAction): MainScreenState =
        when (toolbarAction) {
            MainToolbarAction.CheckUpdate -> handleCheckUpdateClicked()
            MainToolbarAction.Rate -> handleRateClicked()
            MainToolbarAction.Share -> handleShareClicked()
            MainToolbarAction.Upgrade -> handleUpgradeClicked()
            MainToolbarAction.Help -> handleHelpClicked()
            MainToolbarAction.ShowAd -> handleShowAdClicked()
            MainToolbarAction.AdFreeHour -> handleAdFreeHourClicked()
            MainToolbarAction.RestorePurchase -> handleRestorePurchaseClicked()
            MainToolbarAction.ConsumeTestPurchase -> handleConsumeTestPurchaseClicked()
        }

    private fun handleAdFreeHourClicked(): MainScreenState {
        val adFreeUntil = settings.getLong(SharedPrefsConstants.AD_FREE_UNTIL_MILLIS, 0L)
        if (currentMilliseconds < adFreeUntil) {
            val formattedTime = formatTime(adFreeUntil)
            val message = SharedRes.strings.ads_already_disabled_format.format(formattedTime)
            onlyEffect(MainScreenEffect.ShowToast(message))
            return currentState
        }

        analyticsProvider.logEvent(AnalyticsConstants.SHOW_REWARDED_AD)
        return currentState.copy(dialogState = DialogState.ConfirmRewardedAd)
    }

    private fun handleCheckUpdateClicked(): MainScreenState {
        onlyEffect(MainScreenEffect.CheckUpdate)
        analyticsProvider.logEvent(
            event = AnalyticsConstants.CHECK_UPDATE,
        )
        return currentState
    }

    private fun handleRateClicked(): MainScreenState {
        onlyEffect(MainScreenEffect.RateApp)
        analyticsProvider.logEvent(
            event = AnalyticsConstants.RATE,
        )
        reviewPrompter.onManualRateClicked()
        return currentState
    }

    /**
     * The main menu came on screen. Suspends in the caller's scope, so that leaving the menu
     * cancels a review request that has not gone out yet.
     */
    suspend fun onMainMenuShown() = reviewPrompter.onMainMenuShown()

    private fun handleRemoveAdsButtonShown(): MainScreenState {
        analyticsProvider.logEvent(AnalyticsConstants.Purchase.EVENT_BUTTON_SHOWN)
        return currentState
    }

    private fun handleShareClicked(): MainScreenState {
        launchEffect {
            shareAppUseCaseCommon()
        }
        analyticsProvider.logEvent(
            event = AnalyticsConstants.SHARE_APP,
        )
        return currentState
    }

    private fun handleUpgradeClicked(): MainScreenState {
        onlyEffect(MainScreenEffect.Upgrade)
        return currentState
    }

    private fun handleShowAdClicked(): MainScreenState {
        val adFreeUntil = settings.getLong(SharedPrefsConstants.AD_FREE_UNTIL_MILLIS, 0L)
        if (currentMilliseconds < adFreeUntil) {
            val formattedTime = formatTime(adFreeUntil)
            val message = SharedRes.strings.ads_already_disabled_format.format(formattedTime)
            onlyEffect(MainScreenEffect.ShowToast(message))
            return currentState
        }

        analyticsProvider.logEvent(event = AnalyticsConstants.SHOW_AD)
        return currentState.copy(dialogState = DialogState.ConfirmSupportAd)
    }

    private fun handleShowAboutTeruma(aboutTerumaUiModel: AboutTerumaUiModel): MainScreenState {
        return currentState.copy(aboutTerumaUiModel = aboutTerumaUiModel)
    }

    companion object {
        private const val REORDER_EVENT_NAME = "Reorder Buttons"
        private const val REORDER_PROPERTY = "NewOrder"
    }

}

/**
 * The Telegram button, for a user whose saved button order predates it: placed right after
 * [ButtonType.RECORDINGS]. An order that already contains it is left alone.
 */
internal fun List<ButtonType>.withTelegramChannel(): List<ButtonType> {
    if (ButtonType.TELEGRAM_CHANNEL in this) return this
    val index = indexOf(ButtonType.RECORDINGS) + 1 // 0 when there is no recordings button
    return toMutableList().apply { add(index, ButtonType.TELEGRAM_CHANNEL) }
}

/**
 * The "חידונים אמיתיים" button ([ButtonType.PAST_QUIZZES]): removed when Remote Config switched
 * it off; otherwise added first, for a user whose saved button order predates it. An order that
 * already contains it is left alone, so a user who moved it keeps it where they put it.
 */
internal fun List<ButtonType>.withPastQuizzes(isEnabled: Boolean): List<ButtonType> {
    if (!isEnabled) return filterNot { it == ButtonType.PAST_QUIZZES }
    if (ButtonType.PAST_QUIZZES in this) return this
    return listOf(ButtonType.PAST_QUIZZES) + this
}

/**
 * The widget's button ([ButtonType.VERSE_WIDGET]): removed when it is switched off (Remote Config, or
 * a platform without home-screen widgets); otherwise added right after the random-verse button, for a
 * user whose saved button order predates it. An order that already contains it is left alone, so a
 * user who moved it keeps it where they put it.
 */
internal fun List<ButtonType>.withVerseWidget(isEnabled: Boolean): List<ButtonType> {
    if (!isEnabled) return filterNot { it == ButtonType.VERSE_WIDGET }
    if (ButtonType.VERSE_WIDGET in this) return this
    val index = indexOf(ButtonType.RANDOM_VERSE) + 1 // 0 when there is no random-verse button
    return toMutableList().apply { add(index, ButtonType.VERSE_WIDGET) }
}
